import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Progress, Typography } from "antd";

import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer.js";
import ThinCircleIcon from "@/assets/icons/thin-circle.svg?react";
import ThickCircleIcon from "@/assets/icons/thick-circle.svg?react";
import CircleCheckIcon from "@/assets/icons/check-circle.svg?react";
import {
  SectionProgressBarProps,
  SectionProgressCircleProps,
} from "@/features/survey/form/types";
import { SectionStatisticsResponse } from "@/redux/survey/types";

const { Text } = Typography;

const SectionProgressCircle = ({
  requiredQuestionsCount,
  answeredRequiredQuestionsCount,
  color,
}: SectionProgressCircleProps) => {
  if (answeredRequiredQuestionsCount === 0) {
    return (
      <BaseSvgRenderer
        width="16"
        fill={"var(--neutral-300)"}
        component={ThinCircleIcon}
      />
    );
  } else if (answeredRequiredQuestionsCount >= requiredQuestionsCount) {
    return (
      <BaseSvgRenderer
        width="20"
        fill={color}
        component={CircleCheckIcon}
        stroke="var(--base-white)"
      />
    );
  } else
    return (
      <BaseSvgRenderer width="16" fill={color} component={ThickCircleIcon} />
    );
};

const SectionProgressBar = ({ sections }: SectionProgressBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sectionId } = useParams();

  const currentSectionId = sectionId ? Number(sectionId) : null;

  const calculatePercentage = (section: SectionStatisticsResponse) => {
    return section.requiredQuestionsCount === 0
      ? 100
      : Math.min(
          section.answeredRequiredQuestionsCount /
            section.requiredQuestionsCount,
          1,
        ) * 100;
  };

  return (
    <div className="flex gap-200">
      {sections
        .slice()
        .sort((a, b) => a.index - b.index)
        .map((section) => {
          const percentage = calculatePercentage(section);

          return (
            <div
              key={section.id}
              style={{ cursor: "pointer", flex: 1 }}
              onClick={() =>
                navigate(
                  `/reports/${section.survey}/section/${section.id}${location.search}`,
                )
              }
            >
              <Progress
                percent={percentage}
                className={`section-progress ${currentSectionId == section.id && "section-progress-active"}`}
                size="small"
                strokeColor={
                  currentSectionId == section.id
                    ? section.color
                    : `${section.color}60`
                }
              />

              <div className="flex items-center gap-100">
                <SectionProgressCircle
                  requiredQuestionsCount={section.requiredQuestionsCount}
                  answeredRequiredQuestionsCount={
                    section.answeredRequiredQuestionsCount
                  }
                  color={
                    currentSectionId == section.id
                      ? section.color
                      : `${section.color}60`
                  }
                />
                <Text
                  className={`section-progress-bar-label ${currentSectionId == section.id ? "body-s-medium" : "body-s-regular"}`}
                >
                  {section.title}
                </Text>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default SectionProgressBar;
